import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Clock } from 'lucide-react';
import { getPurchaseStatus } from '../../services/api/creditPurchaseService';

/* 旧 styled-components 定義をクラス文字列定数として保持。
 * font-size は line-height 未指定 or 別指定なので arbitrary value で保持する。 */
const CONTAINER = 'flex items-center justify-center min-h-screen bg-gray-50'; /* #F9FAFB */

const CARD =
  'bg-white rounded-tasky-lg p-12 text-center shadow-[0_4px_24px_rgba(0,0,0,0.08)] ' +
  'max-w-[400px] w-[90%]';

const ICON_WRAPPER_BASE = 'mb-4';
const ICON_WRAPPER_VARIANT: Record<'success' | 'error' | 'pending', string> = {
  success: 'text-emerald-500', // #10B981
  pending: 'text-amber-500', // #F59E0B
  error: 'text-red-500', // #EF4444
};

const TITLE = 'text-[20px] font-semibold text-gray-900 m-0 mb-2'; /* #111827 */

const DESCRIPTION = 'text-[14px] text-gray-500 m-0 mb-6 leading-[1.6]'; /* #6B7280 */

const REDIRECT_NOTE = 'text-[12px] text-gray-400 m-0 mb-4'; /* #9CA3AF */

const HOME_BUTTON =
  'px-6 py-2.5 bg-indigo-500 text-white border-0 rounded-tasky-sm text-[14px] font-medium ' +
  'cursor-pointer transition-[background] duration-150 hover:bg-indigo-600'; /* #6366F1 / #4F46E5 */

interface CreditPurchaseResultPageProps {
  type: 'success' | 'cancel';
}

const CreditPurchaseResultPage: React.FC<CreditPurchaseResultPageProps> = ({ type }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // 銀行振込など非同期決済では success 画面に来ても未入金 (pending) の場合がある。
  // session_id から購入ステータスを引き、pending なら「入金確認中」を表示する。
  const [isPending, setIsPending] = useState(false);
  // status 確認が済むまで自動遷移を保留する (pending を取りこぼさないため)。cancel は即確定。
  const [statusChecked, setStatusChecked] = useState(type !== 'success');
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    if (type !== 'success') return;
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setStatusChecked(true);
      return;
    }
    let active = true;
    getPurchaseStatus(sessionId)
      .then((res) => {
        if (active) setIsPending(res.is_pending);
      })
      .catch(() => {
        // 取得失敗時は通常の完了表示にフォールバック (付与自体は webhook で行われる)。
      })
      .finally(() => {
        if (active) setStatusChecked(true);
      });
    return () => {
      active = false;
    };
  }, [type, searchParams]);

  // 入金確認中 (pending) のときは自動遷移しない。それ以外は確認完了後にカウントダウン。
  useEffect(() => {
    if (!statusChecked || isPending) return;
    const timer = setInterval(() => {
      setCountdown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [statusChecked, isPending]);

  // 遷移は state 更新関数の中ではなく effect で行う（更新関数内の navigate は描画中の更新警告になる）。
  useEffect(() => {
    if (!statusChecked || isPending || countdown > 0) return;
    navigate('/home');
  }, [navigate, statusChecked, isPending, countdown]);

  const renderContent = () => {
    if (type === 'cancel') {
      return (
        <>
          <div className={`${ICON_WRAPPER_BASE} ${ICON_WRAPPER_VARIANT.error}`}>
            <XCircle size={48} />
          </div>
          <h1 className={TITLE}>購入がキャンセルされました</h1>
          <p className={DESCRIPTION}>クレジットの購入がキャンセルされました。</p>
        </>
      );
    }
    if (isPending) {
      return (
        <>
          <div className={`${ICON_WRAPPER_BASE} ${ICON_WRAPPER_VARIANT.pending}`}>
            <Clock size={48} />
          </div>
          <h1 className={TITLE}>入金確認中です</h1>
          <p className={DESCRIPTION}>
            お振込みが確認できしだいクレジットを付与します。確認には数十分〜数営業日かかる場合があります。
          </p>
        </>
      );
    }
    return (
      <>
        <div className={`${ICON_WRAPPER_BASE} ${ICON_WRAPPER_VARIANT.success}`}>
          <CheckCircle size={48} />
        </div>
        <h1 className={TITLE}>購入が完了しました</h1>
        <p className={DESCRIPTION}>クレジットがアカウントに追加されました。</p>
      </>
    );
  };

  return (
    <div className={CONTAINER}>
      <div className={CARD}>
        {renderContent()}
        {statusChecked && !isPending && (
          <p className={REDIRECT_NOTE}>{countdown}秒後に自動的にホームに戻ります...</p>
        )}
        <button className={HOME_BUTTON} onClick={() => navigate('/home')}>ホームに戻る</button>
      </div>
    </div>
  );
};

export default CreditPurchaseResultPage;
